type CreateThrottledCallbackOptions = {
  callback: () => void;
  getTime?: () => number;
  throttleMs: number;
};

/**
 * Creates a throttled callback that only invokes the underlying callback
 * if at least `throttleMs` milliseconds have elapsed since the last invocation.
 *
 * @param options Configuration options
 * @param options.callback The function to throttle
 * @param options.throttleMs Minimum time between invocations in milliseconds
 * @param options.getTime Function to get current time (defaults to Date.now, injectable for testing)
 * @returns A throttled version of the callback that returns true if invoked, false if throttled
 */
const createThrottledCallback = ({
  callback,
  getTime = Date.now,
  throttleMs,
}: CreateThrottledCallbackOptions): (() => boolean) => {
  const lastCallTimeRef = { current: getTime() - throttleMs };

  return () => {
    const now = getTime();
    const timeSinceLastCall = now - lastCallTimeRef.current;

    if (timeSinceLastCall < throttleMs) return false;

    lastCallTimeRef.current = now;
    callback();
    return true;
  };
};

export { createThrottledCallback };
